import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysis } from '../context/AnalysisContext';
import Header from '../components/Header';
import TabQuantitativos from '../components/TabQuantitativos';
import TabMetodo from '../components/TabMetodo';
import TabDownload from '../components/TabDownload';

const TABS = [
  { id: 'quantitativos', label: 'Quantitativos', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
  { id: 'metodo', label: 'Método Executivo', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  { id: 'download', label: 'Download', icon: 'M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
];

function Results() {
  const [activeTab, setActiveTab] = useState('quantitativos');
  const { analysisData } = useAnalysis();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!analysisData) {
      navigate('/upload');
    } else {
      setItems(analysisData.quantitativos || []);
    }
  }, [analysisData, navigate]);

  if (!analysisData) return null;

  const { resumo, fileName } = analysisData;

  const handleAddItem = (item) => {
    setItems((prev) => [...prev, item]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
          Resultados da Análise
        </h1>
        <p className="text-gray-500 mb-8">
          {resumo?.descricao || 'Confira os quantitativos extraídos e configure o método executivo'}
          {fileName && (
            <span className="ml-2 text-sm text-gray-400">({fileName})</span>
          )}
        </p>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-8 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium text-sm md:text-base
                transition-all duration-200 whitespace-nowrap cursor-pointer
                ${activeTab === tab.id
                  ? 'bg-white text-orca-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                }
              `}
            >
              <svg className="w-5 h-5 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          {activeTab === 'quantitativos' && <TabQuantitativos data={items} resumo={resumo} onAddItem={handleAddItem} />}
          {activeTab === 'metodo' && <TabMetodo />}
          {activeTab === 'download' && <TabDownload data={items} resumo={resumo} fileName={fileName} />}
        </div>

        {/* CUB Budget CTA */}
        <div className="mt-6 bg-gradient-to-r from-orca-50 to-blue-50 rounded-2xl border border-orca-200 p-6 flex flex-col sm:flex-row items-center gap-4">
          <div className="w-12 h-12 bg-orca-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-orca-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-bold text-gray-900">Estimar custo pelo CUB</h3>
            <p className="text-sm text-gray-500">Calcule o custo estimado da obra usando o Custo Unitario Basico (CUB/m2)</p>
          </div>
          <button
            onClick={() => navigate(`/orcamento-cub?area=${resumo?.area_total || ''}&tipo=${encodeURIComponent(resumo?.tipo_projeto || '')}`)}
            className="px-6 py-3 text-sm font-bold text-white bg-orca-800 rounded-xl hover:bg-orca-700 cursor-pointer transition-colors whitespace-nowrap"
          >
            Calcular com CUB
          </button>
        </div>

        <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-6 flex flex-col sm:flex-row items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-bold text-gray-900">Orçamento detalhado com SINAPI</h3>
            <p className="text-sm text-gray-500">Monte um orçamento analítico usando composições e preços SINAPI (CAIXA)</p>
          </div>
          <button
            onClick={() => navigate('/orcamento-sinapi')}
            className="px-6 py-3 text-sm font-bold text-white bg-green-700 rounded-xl hover:bg-green-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            Orçamento SINAPI
          </button>
        </div>
      </main>
    </div>
  );
}

export default Results;
